// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

let adminVerificationAccessCache = null;
let adminVerificationAccessPromise = null;

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Initiate a non-privileged transaction through the server so immutable
// transaction workflow requirements can be snapshotted from the listing.
export const initiateWithWorkflowSnapshot = body => {
  return post('/api/initiate-with-workflow-snapshot', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

// update listing
export const updateListingBids = body => {
  return post(`/api/listing/update-listing-bids`, body);
};

// Bidding
export const initiateBiddingPrivileged = body => {
  return post(`/api/initiate-bidding-privileged`, body);
};

// Bulk sales agreement uploads
export const presignBulkSalesAgreement = body => {
  return post('/api/agreements/presign', body);
};

export const attachBulkSalesAgreement = body => {
  return post('/api/agreements/attach', body);
};

export const deleteBulkSalesAgreement = body => {
  return post('/api/agreements/delete', body);
};

export const generateDualBsas = body => {
  return post('/api/agreements/generate-dual', body);
};

export const sendDualBsasForSignature = body => {
  return post('/api/agreements/send-docusign', body);
};

export const reconcileDocusignAgreements = body => {
  return post('/api/agreements/reconcile-docusign', body);
};

export const previewRoleAgreement = body => {
  return post('/api/agreements/preview', body);
};

export const collectFinalPayment = body => {
  return post('/api/agreements/final-payment', body);
};

export const getDocusignSigningView = ({ transactionId, returnUrl } = {}) => {
  const params = new URLSearchParams();
  if (transactionId) {
    params.set('transactionId', transactionId);
  }
  if (returnUrl) {
    params.set('returnUrl', returnUrl);
  }

  const query = params.toString();
  const path = query ? `/api/docusign/signing-view?${query}` : '/api/docusign/signing-view';

  return request(path, {
    method: methods.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const createDocusignRecipientView = ({ transactionId, envelopeId, returnUrl } = {}) => {
  if (!envelopeId) {
    return getDocusignSigningView({ transactionId, returnUrl });
  }

  const encodedEnvelopeId = encodeURIComponent(envelopeId);
  const path = `/api/docusign/envelopes/${encodedEnvelopeId}/recipient-view`;
  return post(path, {
    transactionId,
    returnUrl,
  });
};

export const initiateAchPayment = body => {
  return post('/api/payments/ach/initiate', body);
};

export const initiateCaskDownPayment = body => {
  return post('/api/casks/downpayment/initiate', body);
};

export const createFinalPaymentLink = body => {
  return post('/api/final-payment/link', body);
};

export const notifyOfferEvent = body => {
  return post('/api/offer-notifications', body);
};

export const notifyInternalTransactionEvent = body => {
  return post('/api/internal-notifications', body);
};

export const syncMarketplaceNotifications = _body => {
  return Promise.resolve({
    data: {
      skipped: true,
      eventsTriggered: 0,
      latestEventId: null,
    },
  });
};

export const getFinalPaymentSession = ({ transactionId, token } = {}) => {
  const params = new URLSearchParams();
  if (transactionId) {
    params.set('transactionId', transactionId);
  }
  if (token) {
    params.set('token', token);
  }

  const query = params.toString();
  const path = query ? `/api/final-payment/session?${query}` : '/api/final-payment/session';

  return request(path, {
    method: methods.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const createFinalPaymentIntent = body => {
  return post('/api/final-payment/create-intent', body);
};

export const createFinalPaymentAchIntent = body => {
  return post('/api/final-payment/create-ach', body);
};

export const confirmFinalPayment = body => {
  return post('/api/final-payment/confirm', body);
};

export const contactChevalier = body => {
  return post('/api/contact-chevalier', body);
};

export const contactUs = body => {
  return post('/api/contact', body);
};

export const subscribeToMailchimp = body => {
  return post('/api/mailchimp/subscribe', body);
};

const createQueryString = params => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null) {
      return;
    }

    const normalizedValue = typeof value === 'string' ? value.trim() : value;
    if (normalizedValue === '') {
      return;
    }

    search.set(key, String(normalizedValue));
  });

  const query = search.toString();
  return query ? `?${query}` : '';
};

export const getAdminVerificationUsers = params => {
  const query = createQueryString(params);
  return request(`/api/admin/verification/users${query}`, {
    method: methods.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const approveAdminVerificationUser = (userId, body = {}) => {
  return post(`/api/admin/verification/users/${userId}/approve`, body);
};

export const rejectAdminVerificationUser = (userId, body = {}) => {
  return post(`/api/admin/verification/users/${userId}/reject`, body);
};

export const requestInfoAdminVerificationUser = (userId, body = {}) => {
  return post(`/api/admin/verification/users/${userId}/request-info`, body);
};

export const buildAdminVerificationCertificateUrl = (userId, { download = false } = {}) => {
  const safeUserId = encodeURIComponent(String(userId || '').trim());
  const query = download ? '?download=1' : '';
  return `${apiBaseUrl()}/api/admin/verification/users/${safeUserId}/certificate${query}`;
};

export const uploadSellerVerificationCertificate = file => {
  const fileName = file?.name ? encodeURIComponent(file.name) : 'seller-verification-attachment';
  const contentType = file?.type || 'application/octet-stream';

  return window
    .fetch(`${apiBaseUrl()}/api/seller-verification/certificate`, {
      method: methods.POST,
      credentials: 'include',
      headers: {
        'Content-Type': contentType,
        'x-file-name': fileName,
      },
      body: file,
    })
    .then(res => {
      if (res.status >= 400) {
        return res.json().then(data => {
          let error = new Error();
          error = Object.assign(error, data);
          throw error;
        });
      }

      return res.json();
    });
};

export const resetAdminVerificationAccessCache = () => {
  adminVerificationAccessCache = null;
  adminVerificationAccessPromise = null;
};

export const checkAdminVerificationAccess = () => {
  if (typeof adminVerificationAccessCache === 'boolean') {
    return Promise.resolve(adminVerificationAccessCache);
  }

  if (adminVerificationAccessPromise) {
    return adminVerificationAccessPromise;
  }

  adminVerificationAccessPromise = request('/api/admin-verification/access', {
    method: methods.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(response => {
      const allowed = Boolean(response?.data?.allowed);
      adminVerificationAccessCache = allowed;
      adminVerificationAccessPromise = null;
      return allowed;
    })
    .catch(() => {
      adminVerificationAccessPromise = null;
      return false;
    });

  return adminVerificationAccessPromise;
};
